import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ShoppingCart } from "lucide-react";
import { addToCart, setCartItems } from "../../redux/cartSlice";
import { AuthContext } from "../../context/AuthContext";
import { apiFetch } from "../../utils/api";
import "../../styles/sectionPage.css";

/**
 * Presentational component for a single Product Card.
 * Renders as a vertical card on desktop and a horizontal row on mobile.
 * The entire card is clickable, routing to /product/:id.
 * @param {Object} props
 * @param {Object} props.product - The product database document
 * @param {string} props.sectionColor - Accent color for styling
 */
const ProductCard = ({ product, sectionColor }) => {
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const cartItems = useSelector((state) => state.cart.cartItems) || [];

  if (!product) return null;

  // Resolve product image from Cloudinary array or seed fallback
  const getProductImage = () => {
    if (product.images && product.images[0] && product.images[0].url) {
      return product.images[0].url;
    }
    if (product.imageUrl) {
      return product.imageUrl;
    }
    return "/placeholder_product.png";
  };

  const formattedMoq = () => {
    const count = product.moq || 1;
    if (product.moqType === "set") {
      return `MOQ: ${count} Sets${product.unitsPerSet ? ` (${product.unitsPerSet} Pcs/Set)` : ""}`;
    }
    return `MOQ: ${count} Pcs`;
  };

  const categoryName = product.category?.name || "General";

  const handleAddToCartClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check availability
    const isAvailable = product.isAvailable === true;
    if (!isAvailable) {
      alert("Product is currently unavailable.");
      return;
    }

    const isAlreadyInCart = cartItems.some((item) => item.productId === product._id);
    if (isAlreadyInCart) {
      alert("Product already in your cart!");
      return;
    }

    const isSet = product.moqType === 'set';
    const qty = isSet 
      ? (product.moq || 1) * (product.unitsPerSet || 1) 
      : (product.moq || 1);

    const imageUrls = product.images?.map((img) => 
      typeof img === 'string' ? img : img?.url
    ).filter(Boolean) || [];
    
    const primaryImage = imageUrls[0] || product.imageUrl || '/placeholder_product.png';

    if (user && user.token) {
      try {
        const res = await apiFetch('/api/cart', {
          method: 'POST',
          body: JSON.stringify({ productId: product._id, qty })
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.message || 'Failed to add product to cart');
          return;
        }
        dispatch(setCartItems(data));
        alert('Successfully added to your cart!');
      } catch (err) {
        console.error(err);
        alert('Failed to add product to cart');
      }
    } else {
      dispatch(
        addToCart({
          productId: product._id,
          name: product.name,
          price: product.price,
          image: primaryImage,
          imageUrl: primaryImage,
          images: imageUrls,
          qty: qty,
          section: product.section,
          category: categoryName,
          moqType: product.moqType || 'pcs',
          unitsPerSet: product.unitsPerSet || 1,
          isAvailable: product.isAvailable
        })
      );
      alert('Successfully added to your cart!');
    }
  };
  
  // Section dynamic accents
  const cardStyle = {
    "--section-color": sectionColor || "#ff6a00",
    "--section-color-glow": sectionColor && sectionColor.startsWith("#") ? `${sectionColor}26` : "rgba(255, 255, 255, 0.15)",
  };

  return (
    <Link to={`/product/${product._id}`} className="product-card" style={cardStyle}>
      {/* 1. Left/Top Product Image Container */}
      <div className="product-card-image-wrapper">
        <img
          src={getProductImage()}
          alt={product.name}
          className="product-card-image"
          loading="lazy"
        />
        {/* Category Label overlay on desktop */}
        <span className="product-card-category-tag">{categoryName}</span>
      </div>

      {/* 2. Right/Bottom Product Details */}
      <div className="product-card-info">
        <div>
          {/* Category text helper (Mobile only) */}
          <span className="product-card-mobile-category">{categoryName}</span>
          <h3 className="product-card-name">{product.name}</h3>
          <p className="product-card-description">{product.description}</p>
        </div>

        <div>
          {/* Metadata: Price and MOQ */}
          <div className="product-card-meta">
            <span className="product-card-price">
              ₹{product.price}
              <span className="price-unit"> /pc</span>
            </span>
            <span className="product-card-moq">{formattedMoq()}</span>
          </div>

          {/* Actions: Add to Cart & View Details */}
          <div className="product-card-actions">
            <button 
              onClick={handleAddToCartClick}
              className="product-card-btn-cart"
            >
              <ShoppingCart size={16} />
              <span>Add to Cart</span>
            </button>
            <span className="product-card-btn-details">
              Details
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
